import { Children, isValidElement, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import type { MDXRemoteProps } from "next-mdx-remote";

import {
	ArticleH2,
	ArticleH3,
	articleMdxComponents,
} from "../article/mdx-components";
import { LegalContact } from "./legal-contact";

/** Flattens a heading's children to plain text, ignoring any inline markup. */
function toText(node: ReactNode): string {
	return Children.toArray(node)
		.map((child) => {
			if (typeof child === "string" || typeof child === "number") {
				return String(child);
			}
			if (isValidElement<{ children?: ReactNode }>(child)) {
				return toText(child.props.children);
			}
			return "";
		})
		.join("");
}

/** "Regional rights" -> "regional-rights". Matches GitHub's heading slugs. */
function slugify(node: ReactNode): string {
	return toText(node)
		.toLowerCase()
		.replace(/[^\w\s-]/g, "")
		.trim()
		.replace(/\s+/g, "-");
}

/**
 * The MDX map for the legal collection: the article prose styling, plus the
 * things legal documents need that articles never did.
 */
export const legalMdxComponents: MDXRemoteProps["components"] = {
	...articleMdxComponents,

	// Legal docs cross-reference their own clauses ("see Sub-processors below"),
	// and procurement teams link to a specific section. The article headings
	// carry no id, so those links would silently go nowhere — slug them.
	h2: ({ children }) => (
		<ArticleH2 id={slugify(children)}>{children}</ArticleH2>
	),
	h3: ({ children }) => (
		<ArticleH3 id={slugify(children)}>{children}</ArticleH3>
	),

	// Every outbound link in the legal pages opens in a new tab; the article map
	// never sets target/rel. Internal links (/security) stay in-tab.
	a: ({ children, href }) => {
		const external = href?.startsWith("http");
		return (
			<Link
				href={href}
				target={external ? "_blank" : undefined}
				rel={external ? "noopener" : undefined}
				sx={{ color: "var(--mui-palette-primary-main)" }}>
				{children}
			</Link>
		);
	},

	// Terms numbers its sub-clauses with roman numerals, authored as a raw
	// `<ol type="i">`. The article map drops `type`, which would silently
	// renumber them 1, 2, 3.
	ol: ({ children, type }) => (
		<Box
			component="ol"
			type={type}
			sx={{ pl: 3, my: 2.5, fontSize: "1.0625rem", lineHeight: 1.7 }}>
			{children}
		</Box>
	),

	LegalContact,
};
